import { Button } from '@99designs/common/components/Button';
import { ButtonGroup, ButtonGroupItem } from '@99designs/common/components/ButtonGroup';
import { Icon } from '@99designs/common/components/Icon';
import { LoadableElement } from '@99designs/common/components/LoadableElement';
import {
    getPhotographyBriefWithPaymentData,
    getPhotographyBriefWithPaymentData_smartShootPaymentData as SmartShootPhotographyPaymentData,
    getPhotographyBriefWithPaymentData_structuredBrief_fields_SmartShootPhotographyFields,
    getPhotographyBriefWithPaymentDataVariables,
} from '__generated__/getPhotographyBriefWithPaymentData';
import {
    getVideoBriefWithPaymentData,
    getVideoBriefWithPaymentData_smartShootPaymentData as SmartShootVideoPaymentData,
    getVideoBriefWithPaymentData_structuredBrief_fields_SmartShootVideoFields,
    getVideoBriefWithPaymentDataVariables,
} from '__generated__/getVideoBriefWithPaymentData';
import { BriefState, SmartShootPaymentDataInput } from '__generated__/types';
import { handleGraphQLError } from 'common/utils/Apollo';
import { Currency, toUnit } from 'common/utils/currency';
import BriefFooter from 'projects/components/SmartShoot/BriefFooter';
import BriefPaymentContent from 'projects/components/SmartShoot/BriefPaymentContent';
import BriefPaymentHero from 'projects/components/SmartShoot/BriefPaymentHero';
import SmartShootUSOnly from 'projects/components/SmartShoot/SmartShootUSOnly';
import { trackProjectPaymentSuccessful, trackViewedBriefEvent } from 'projects/events';
import { routes } from 'projects/routes';
import { history } from 'projects/routes/BrowserRouter';
import photographyQueries from 'projects/routes/SmartShootPhotographyBrief.graphql';
import videoQueries from 'projects/routes/SmartShootVideoBrief.graphql';
import { PaymentData } from 'projects/services/PaymentData';
import PaymentService from 'projects/services/PaymentService';
import React from 'react';
import { Query } from 'react-apollo';
import { redirectToSmartShootGetStarted } from './index';

// Source: https://docs.google.com/spreadsheets/d/1tNbH_OAe_rJPTjhbpdyETTg5Z38ZzbawqHl1_7sg8aM/edit#gid=18210568
const PROFIT_MARGIN = 0.3;

interface Props {
    briefId: string;
    briefType: 'video-production' | 'photo';
}

class SmartShootBriefPayment extends React.Component<Props> {
    private paymentService: PaymentService;

    constructor(props: Props) {
        super(props);
        this.handleEditRequest = this.handleEditRequest.bind(this);
        this.handlePaymentRequest = this.handlePaymentRequest.bind(this);
    }

    componentDidMount() {
        document.title = 'Your summary | 99designs';
        trackViewedBriefEvent({ id: this.props.briefId, page: 'Summary' });
    }

    componentWillUnmount() {
        this.paymentService.destroy();
    }

    footerActions() {
        return (
            <ButtonGroup variations={['responsive']} style={{ color: '#ff7e65' }}>
                <ButtonGroupItem>
                    <Button
                        onClick={this.handleEditRequest}
                        variations={['marketing-secondary', 'jumbo', 'chameleon', 'responsive']}
                    >
                        Edit my brief
                    </Button>
                </ButtonGroupItem>
                <ButtonGroupItem>
                    <Button
                        variations={['primary', 'jumbo', 'responsive']}
                        onClick={this.handlePaymentRequest}
                        icon={<Icon type={'server-1'} variations={['offset-right']} />}
                    >
                        Continue to payment
                    </Button>
                </ButtonGroupItem>
            </ButtonGroup>
        );
    }

    getBriefPaymentContent() {
        const videoQuery = (
            <Query<getVideoBriefWithPaymentData, getVideoBriefWithPaymentDataVariables>
                query={videoQueries.getVideoBriefWithPaymentData}
                variables={{ id: this.props.briefId, paymentRequest: this.createPaymentRequest() }}
            >
                {({ data, error, loading }) => {
                    if (loading) {
                        return <LoadableElement isLoading={true} variations={['relative']} />;
                    }

                    if (error) {
                        handleGraphQLError(error);
                    }

                    if (this.isDataFetched(data)) {
                        if (data!.structuredBrief.state === BriefState.LAUNCHED) {
                            redirectToSmartShootGetStarted(
                                data!.structuredBrief.foreignKeys[0].toString()
                            );
                        }

                        this.initPaymentService(data!.smartShootPaymentData);
                        return (
                            <BriefPaymentContent
                                briefFields={
                                    data!.structuredBrief
                                        .fields as getVideoBriefWithPaymentData_structuredBrief_fields_SmartShootVideoFields
                                }
                                smartShootPaymentData={data!.smartShootPaymentData}
                                industries={data!.industries}
                            />
                        );
                    }
                    return null;
                }}
            </Query>
        );

        const photographyQuery = (
            <Query<getPhotographyBriefWithPaymentData, getPhotographyBriefWithPaymentDataVariables>
                query={photographyQueries.getPhotographyBriefWithPaymentData}
                variables={{ id: this.props.briefId, paymentRequest: this.createPaymentRequest() }}
            >
                {({ data, error, loading }) => {
                    if (loading) {
                        return <LoadableElement isLoading={true} variations={['relative']} />;
                    }

                    if (error) {
                        handleGraphQLError(error);
                    }

                    if (this.isDataFetched(data)) {
                        if (data!.structuredBrief.state === BriefState.LAUNCHED) {
                            redirectToSmartShootGetStarted(
                                data!.structuredBrief.foreignKeys[0].toString()
                            );
                        }

                        this.initPaymentService(data!.smartShootPaymentData);
                        return (
                            <BriefPaymentContent
                                briefFields={
                                    data!.structuredBrief
                                        .fields as getPhotographyBriefWithPaymentData_structuredBrief_fields_SmartShootPhotographyFields
                                }
                                smartShootPaymentData={data!.smartShootPaymentData}
                                industries={data!.industries}
                            />
                        );
                    }
                    return null;
                }}
            </Query>
        );

        return this.isVideoBrief() ? videoQuery : photographyQuery;
    }

    render() {
        return (
            <SmartShootUSOnly>
                <BriefPaymentHero briefType={this.isVideoBrief() ? 'video' : 'photo'} />
                {this.getBriefPaymentContent()}
                <BriefFooter>{this.footerActions()}</BriefFooter>
            </SmartShootUSOnly>
        );
    }

    private isVideoBrief() {
        return this.props.briefType === 'video-production';
    }

    private isDataFetched(data: any) {
        return (
            data &&
            data.structuredBrief &&
            data.structuredBrief.fields &&
            data.smartShootPaymentData
        );
    }

    private handlePaymentSuccess(e: any) {
        const payment = e.detail.payment;
        const usdProfitInCents = Math.round(payment.amount * PROFIT_MARGIN);

        const currency: Currency = (Currency as any)[payment.currency];
        const usdProfitInDollars = toUnit(usdProfitInCents, currency);

        trackProjectPaymentSuccessful({
            currency: payment.currency,
            profit: usdProfitInDollars,
            usdProfitInCents: usdProfitInCents,
            exchangeRate: 1,
        });
    }

    private handlePaymentFailure() {
        console.error('Payment failed');
    }

    private handlePaymentVisible() {
        trackViewedBriefEvent({ id: this.props.briefId, page: 'Payment' });
    }

    private handleEditRequest() {
        if (this.isVideoBrief()) {
            history.push(routes.videoBrief(this.props.briefId));
        } else {
            history.push(routes.photoBrief(this.props.briefId));
        }
    }

    private handlePaymentRequest() {
        this.paymentService.showModal();
    }

    private initPaymentService(
        paymentData: SmartShootVideoPaymentData | SmartShootPhotographyPaymentData
    ) {
        this.paymentService = new PaymentService({
            paymentInput: this.removeTypename(paymentData),
            onSuccess: this.handlePaymentSuccess.bind(this),
            onFailure: this.handlePaymentFailure.bind(this),
            onVisible: this.handlePaymentVisible.bind(this),
        });
    }

    private createPaymentRequest(): SmartShootPaymentDataInput {
        return {
            briefId: this.props.briefId,
            returnUrl: window.location.href,
            locale: 'en-us',
        };
    }

    private removeTypename(
        data: SmartShootVideoPaymentData | SmartShootPhotographyPaymentData
    ): PaymentData {
        const omitTypename = (key: string, value: any) =>
            key === '__typename' ? undefined : value;
        const newPayload = JSON.parse(JSON.stringify(data), omitTypename);
        return newPayload;
    }
}

export default SmartShootBriefPayment;
